/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, OnInit, signal} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {finalize, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {resetDimensionsBasedOnAdUnitOption} from './ad-unit-commons';
import {AdUnitApiService, AdUnitModel} from './ad-unit-api.service';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

export interface AdUnitCopyResult {
  model: AdUnitModel;
  destination: boolean;
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-ad-unit-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          <span>{{ 'label.copy.adUnit' | ogTranslate }}</span>
          <small>{{ originalName }}</small>
        </h4>
      </div>
      <div class="modal-body">
        @if (copyFailed()) {
          <p class="alert alert-danger">The Ad Unit could not be copied. Try again.</p>
        }
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.name' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="adUnit.name" [(ngModel)]="name" required>
          </div>
        </div>
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.description' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="adUnit.description" [(ngModel)]="description">
          </div>
        </div>
        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="adUnit.destination">
            <span class="small">Go to new ad unit after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="pending()">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class AdUnitCopyModalComponent implements OnInit {
  model: AdUnitModel;

  readonly onCopied = new Subject<AdUnitCopyResult>();

  originalName = '';
  name = '';
  description = '';
  destination = true;
  readonly pending = signal(false);
  readonly copyFailed = signal(false);

  constructor(
    public bsModalRef: BsModalRef,
    private readonly api: AdUnitApiService
  ) {
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
    this.description = this.model?.description || '';
  }

  copy(): void {
    if (this.pending()) {
      return;
    }
    const copy: AdUnitModel = _.cloneDeep({...this.model,
      id: Uuid.generate(),
      name: this.name,
      description: this.description || null
    });
    resetDimensionsBasedOnAdUnitOption(copy);
    this.pending.set(true);
    this.copyFailed.set(false);
    this.api.save(copy).pipe(
      finalize(() => this.pending.set(false))
    ).subscribe({
      next: (saved) => {
        completeCopyModal(this.bsModalRef, this.onCopied, {model: saved, destination: this.destination});
      },
      error: () => this.copyFailed.set(true)
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
